import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, Bell, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import UnitSelector from './shared/UnitSelector';

function Header({ pageTitle, showMenuButton = false, onMenuToggle, menuButtonRef, notificationCount = 0, currentRole }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const location = useLocation();

  const handlePopOut = () => {
    const url = location.pathname + location.search;
    window.open(url, '_blank', 'noopener');
  };

  useEffect(() => {
    if (!showNotifications) return;
    const handleOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [showNotifications]);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-title-wrap">
          {showMenuButton && (
            <Button
              ref={menuButtonRef}
              type="button"
              variant="ghost"
              size="icon"
              onClick={onMenuToggle}
              aria-label="Open sidebar menu"
              title="Open sidebar"
              className="text-text-primary hover:text-white hover:bg-white/10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          )}
          <div className="header-title">{pageTitle || 'Clinical Operations Command Center'}</div>
        </div>

        <div className="header-actions flex items-center gap-2">
          {currentRole === 'manager' && <UnitSelector variant="header" />}

          <Link
            to="/favorites"
            aria-label="Favourites"
            title="Favourites"
            className="header-action-btn flex items-center text-text-primary hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Star className="w-5 h-5" />
          </Link>

          <div ref={notifRef} className="relative">
            <Button
              data-testid="notifications-btn"
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(v => !v)}
              aria-label="Notifications"
              title="Notifications"
              className="relative text-text-primary hover:text-white hover:bg-white/10"
            >
              <Bell className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-critical text-white rounded-full text-[10px] font-bold flex items-center justify-center px-1">
                  {notificationCount}
                </span>
              )}
            </Button>

            {showNotifications && (
              <div className="absolute top-[calc(100%+8px)] right-0 w-[280px] bg-nn-white border border-nn-border rounded-card shadow-md z-tooltip p-4">
                <p className="text-body font-semibold text-text-dark mb-2">Notifications</p>
                <p className="text-body-sm text-text-dark-secondary text-center py-3">
                  No new notifications
                </p>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handlePopOut}
            aria-label="Open in new window"
            title="Open in new window"
            className="ml-auto text-text-primary hover:text-white hover:bg-white/10"
          >
            <ExternalLink className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

export default Header;
